import { motion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, type NotificationType, type OffsetPageInfo, api } from '../api/client';
import type { components } from '../api/schema.gen';
import { NotificationActions } from '../components/NotificationActions';
import { OffsetPager } from '../components/Pagination';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { SelectMenu, type SelectMenuOption } from '../components/SelectMenu';
import { formatDateTimeShort } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

type Notification = components['schemas']['Notification'];

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 20;

const KNOWN_TYPES: NotificationType[] = [
  'key_suspended_abuse',
  'low_balance',
  'free_quota_fallback_billing',
  'topup_requested',
  'added_to_org',
];

/**
 * The full history page LLMR-in-app-notification-ticket-draft.md v2 adds —
 * everything the bell dropdown's own poll only ever shows the latest few
 * of. Offset-paged and type-filterable, same convention as GET
 * /me/security-history (EditProfile.tsx's SecurityHistoryList): changing
 * the filter always resets to page 1, since a page number from the
 * unfiltered list means nothing once the underlying row count changes.
 */
export function Notifications() {
  const { dict } = useLanguage();
  const { isB2BOrg } = useSession();
  const [type, setType] = useState<NotificationType | ''>('');
  const [notifications, setNotifications] = useState<Notification[] | null>(null);
  const [pageInfo, setPageInfo] = useState<OffsetPageInfo | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((p: number, t: NotificationType | '', size: number) => {
    setBusy(true);
    api
      .notifications({ type: t || undefined, page: p, page_size: size })
      .then((res) => {
        setNotifications(res.data);
        setPageInfo(res.page);
        setError(null);
      })
      .catch((e) => {
        setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.notifications.loadError);
      })
      .finally(() => setBusy(false));
  }, [dict.notifications.loadError]);

  useEffect(() => {
    load(page, type, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, pageSize]);

  const typeOptions: SelectMenuOption<NotificationType | ''>[] = [
    { value: '', label: dict.notifications.filterAll },
    ...KNOWN_TYPES.map((t) => ({ value: t, label: dict.notifications.typeLabel(t, isB2BOrg) })),
  ];

  return (
    <div className="page notifications-page">
      <PageHeading subtitle={dict.notifications.historyDescription}>{dict.notifications.historyHeading}</PageHeading>

      <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel notifications-panel">
        <div className="notifications-filter">
          <label htmlFor="notifications-type">{dict.notifications.filterLabel}</label>
          <SelectMenu
            id="notifications-type"
            aria-label={dict.notifications.filterLabel}
            value={type}
            onChange={(v) => {
              setType(v);
              setPage(1);
            }}
            options={typeOptions}
          />
        </div>

        {error && <div className="err">{error}</div>}

        {notifications === null ? (
          <PageLoader />
        ) : notifications.length === 0 ? (
          <p className="note">{dict.notifications.empty}</p>
        ) : (
          <ul className="notifications-list">
            {notifications.map((n) => (
              <li key={n.notification_id} className={n.read_at ? 'is-read' : 'is-unread'}>
                <div className="notifications-list-main">
                  <span className="badge notifications-list-type">{dict.notifications.typeLabel(n.type, isB2BOrg)}</span>
                  <p className="notifications-list-message">{n.message}</p>
                  <span className="notifications-list-time">{formatDateTimeShort(n.created_at)}</span>
                </div>
                <NotificationActions type={n.type} />
              </li>
            ))}
          </ul>
        )}

        <OffsetPager
          page={pageInfo}
          busy={busy}
          onPage={setPage}
          pageSize={pageSize}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </motion.section>
    </div>
  );
}
